import gsap from 'gsap'

/**
 * 360° view (Pano2VR in an iframe), two behaviours the tour can't do for itself.
 *
 * Both reach into the tour's own document — it is served from this origin (see
 * the tour note in home-vr.php), and nothing out here ever covers the iframe, so
 * clicks, drags and the player's buttons always land.
 *
 * @param {import('lenis').default|null} [lenis] smooth-scroll instance
 */
export function initVrTour(lenis) {
  const vrEmbed = document.querySelector('[data-vr-embed]')
  const vrIframe = vrEmbed?.querySelector('[data-vr-iframe]')
  const vrHint = vrEmbed?.querySelector('[data-vr-hint]')
  const vrHintKey = vrHint?.querySelector('[data-vr-hint-key]')
  if (!vrIframe) return

  /**
   * Cooperative wheel — a plain wheel scrolls the page and flashes the hint for
   * 3s, ⌘/Ctrl + wheel zooms the tour.
   *
   * Two things the player forces our hand on: it listens for the legacy
   * `mousewheel`/`DOMMouseScroll` events rather than `wheel`, so blocking `wheel`
   * alone would still let it zoom; and stopping those leaves no default scroll to
   * inherit, so we move the page ourselves.
   */
  const setupCooperativeWheel = (tourDocument) => {
    let hintTimer

    // The zoom modifier is ⌘ on Apple platforms and Ctrl everywhere else. Both
    // handlers below accept either key — only the label has to pick a side.
    // `userAgentData` is Chromium-only, so Safari falls through to `platform`,
    // which still reports MacIntel there (including iPadOS).
    if (vrHintKey) {
      const platform = navigator.userAgentData?.platform || navigator.platform || ''
      vrHintKey.textContent = /mac|iphone|ipad/i.test(platform) ? '⌘' : 'Ctrl'
    }

    /** Show the "hold ⌘/Ctrl to zoom" hint, then fade it out after 3s. */
    const flashHint = () => {
      vrHint.classList.remove('opacity-0')
      clearTimeout(hintTimer)
      hintTimer = setTimeout(() => vrHint.classList.add('opacity-0'), 3000)
    }

    /** Wheel deltas come in lines or pages in some browsers — normalise to px. */
    const deltaPixels = (event) => {
      if (event.deltaMode === 1) return event.deltaY * 16
      if (event.deltaMode === 2) return event.deltaY * window.innerHeight
      return event.deltaY
    }

    // Standard event: drives the page scroll and the hint.
    const onTourWheel = (event) => {
      if (event.metaKey || event.ctrlKey) return // hand over to the player's zoom
      event.preventDefault()
      event.stopPropagation()
      flashHint()
      const delta = deltaPixels(event)
      if (lenis) lenis.scrollTo(lenis.targetScroll + delta, { duration: 0.4 })
      else window.scrollBy(0, delta)
    }

    // Legacy events: dispatched separately from `wheel`, so they need blocking on
    // their own or the player zooms anyway. This is the pair Pano2VR binds.
    const onTourLegacyWheel = (event) => {
      if (event.metaKey || event.ctrlKey) return
      event.preventDefault()
      event.stopPropagation()
    }

    // Capture phase on the document runs ahead of the player's listeners, which
    // sit on its container element.
    const options = { passive: false, capture: true }
    tourDocument.addEventListener('wheel', onTourWheel, options)
    tourDocument.addEventListener('mousewheel', onTourLegacyWheel, options)
    tourDocument.addEventListener('DOMMouseScroll', onTourLegacyWheel, options)
  }

  /**
   * Fullscreen for browsers that have no Fullscreen API — in practice every
   * browser on iPhone, since they are all WebKit and WebKit only offers it on
   * iPad and up. The player's button asks for fullscreen, gets nothing, and
   * falls back to sizing its container to the iframe's window, which is already
   * the whole iframe — so nothing moves. Follow the fullscreen events it emits
   * either way and lift the iframe over the viewport from out here instead.
   *
   * Browsers that can go native are left to it; `allowfullscreen` on the iframe
   * (see home-vr.php) is all they needed.
   */
  const setupFullscreenFallback = (tourDocument, player) => {
    const tourRoot = tourDocument.documentElement
    const canGoNative = Boolean(
      (tourDocument.fullscreenEnabled || tourDocument.webkitFullscreenEnabled) &&
      (tourRoot.requestFullscreen || tourRoot.webkitRequestFullScreen)
    )
    if (canGoNative) return

    // A transformed ancestor becomes the containing block for a fixed child, and
    // the reveal leaves an identity transform on the section wrapper — enough to
    // trap the iframe in the ratio box. Dropping it is free by the time anyone
    // can reach the fullscreen button: the reveal has long finished.
    const revealAncestor = vrEmbed.closest('[data-reveal]')
    let scrollBeforeFullscreen = 0

    const setFullscreen = (fullscreen) => {
      if (fullscreen && revealAncestor) gsap.set(revealAncestor, { clearProps: 'transform' })
      vrEmbed.classList.toggle('is-vr-fullscreen', fullscreen)

      // iOS ignores `overflow: hidden` on <body>, so pin the page instead and
      // put the scroll position back on the way out.
      if (fullscreen) {
        scrollBeforeFullscreen = window.scrollY
        lenis?.stop()
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollBeforeFullscreen}px`
        document.body.style.width = '100%'
      } else {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        window.scrollTo(0, scrollBeforeFullscreen)
        lenis?.start()
      }
    }

    player.addListener('fullscreenenter', () => setFullscreen(true))
    player.addListener('fullscreenexit', () => setFullscreen(false))
  }

  let wheelReady = false
  let fullscreenReady = false

  const setupVr = () => {
    const tourDocument = vrIframe.contentDocument
    // The blank placeholder document the iframe starts on — wait for the real
    // one. Cross-origin reads as null, which is the answer we need right away.
    if (tourDocument?.URL === 'about:blank') return

    // Null only if the tour ever ends up on another origin — bail rather than
    // throw and take down everything set up after this.
    if (!tourDocument) return

    if (!wheelReady && vrHint && window.matchMedia('(pointer: fine)').matches) {
      wheelReady = true
      setupCooperativeWheel(tourDocument)
    }

    // The player is created by an inline script in the tour page, so it only
    // exists once that document has run — usually on the `load` pass below.
    const player = vrIframe.contentWindow?.pano
    if (!fullscreenReady && player) {
      fullscreenReady = true
      setupFullscreenFallback(tourDocument, player)
    }
  }

  vrIframe.addEventListener('load', setupVr)
  setupVr()
}
